/**
 * The page number the app is willing to ask for (066 FR-006, promoted in H17).
 *
 * The address is a place anyone can type, and neither list route fully defends itself:
 *
 *  - `GET /bookings` (`bookings/routes.ts:181`) computes `Math.max(1, Number(query.page ?? 1))`,
 *    and `Math.max` propagates NaN — so `?page=abc` reaches Mongo as `.skip(NaN)`.
 *  - `GET /reviews` (`reviews/routes.ts:51`) computes `Number.parseInt(q.page)` with **no clamp
 *    at all** — so `?page=0` reaches Mongo as `.skip(-20)` and `?page=abc` as `.skip(NaN)`.
 *
 * Correcting the value here WAS the client honouring an obligation the platform never stated. As of
 * 071 the platform states it: every paginated read refuses a page that is not a whole number of 1 or
 * more. This guard therefore stops being a courtesy and becomes the reason a typo in the address
 * renders the first page instead of a refusal.
 *
 * A page that is legal but does not exist — `?page=99` of a 3-page history — is **not** corrected.
 * It is sent, answered with an empty slice, and stated truthfully by the screen. Only values that
 * are not a page at all become 1.
 */
export function toRequestedPage(raw: string | undefined): number {
  // 071: `Number.isSafeInteger`, not `Number.isInteger`. `Number.isInteger(1e21)` is **true**, so
  // `?page=1e21` used to be forwarded verbatim — harmless while the platform silently corrected it,
  // and an error screen now that the platform refuses it (FR-013). A value beyond exact
  // representation is not a page anybody asked for: `9007199254740993` silently becomes ...992.
  //
  // Deliberately NOT the platform's digits-only test. Both screens interpolate the NUMBER this
  // returns (`?page=${page}`), never the raw string, so what reaches the wire is always digits —
  // and copying the stricter rule here would refuse `?page=1e2` in the address bar, where the app
  // renders page 100 today (research R8).
  const n = Number(raw);
  if (!Number.isSafeInteger(n) || n < 1) return 1;
  return n;
}
